import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useCart } from "@/contexts/CartContext";
import type { CartItem } from "@/types/cart";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import { Sheet, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { SheetContent } from "../ui/sheet-content";
import { useQuery } from "@tanstack/react-query";
import { getProducts } from "@/lib/firestore/products";
import { Trash2, Plus, Minus, ShoppingCart, X, ShoppingBag, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/lib/utils/currency";
import { Product } from "@/types/products";
import { Tale } from "@/types/tales";
import { motion, AnimatePresence } from "framer-motion";
import { calculatePriceForSize } from "@/lib/utils/price-calculator";
import { Badge } from "../ui/badge";
import ResponsiveOptimizedImage from "../ResponsiveOptimizedImage";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const cartItemVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.2 } }
};

const emptyCartVariants = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
};

const CartItemComponent = motion.div;

// Extracted to a separate component for better performance
const EmptyCartMessage = React.memo(({ onClose, navigate }: { onClose: () => void, navigate: (path: string) => void }) => (
  <motion.div 
    className="flex flex-col items-center justify-center py-12 text-center"
    variants={emptyCartVariants}
    initial="initial"
    animate="animate"
  >
    <div className="bg-primary/10 rounded-full p-4 mb-4">
      <ShoppingCart className="h-12 w-12 text-primary" />
    </div>
    <h3 className="text-lg font-medium mb-2">Your cart is empty</h3>
    <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
      Looks like you haven't added anything to your cart yet. Start shopping to fill it up!
    </p>
    <Button onClick={() => {
      onClose();
      navigate("/products");
    }}>
      Browse Products
    </Button>
  </motion.div>
));

// Extracted to a separate component for better performance
const CartItemImage = React.memo(({ item }: { item: CartItem }) => {
  let imageSrc = '/placeholder.png';
  
  if (item.image) {
    imageSrc = item.image;
  }

  return (
    <div className="relative h-24 w-24 overflow-hidden rounded-lg bg-secondary/20 border group">
      <ResponsiveOptimizedImage
        src={imageSrc}
        alt={item.name}
        className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
        loading="lazy"
        sizes={[96]} // Since the container is 24rem (96px), we only need one size
        sizesAttribute="96px"
      />
    </div>
  );
});

// Extracted to a separate component for better performance
const CartItemDetails = React.memo(({ 
  item, 
  removeItem 
}: { 
  item: CartItem, 
  removeItem: (itemId: string) => Promise<void> 
}) => (
  <div className="flex flex-1 flex-col">
    <div className="flex justify-between gap-2">
      <div>
        <h3 className="text-base font-medium leading-tight line-clamp-2">{item.name}</h3>
        <div className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
          {item.type === 'tale' ? (
            <div className="space-y-0.5">
              {item.customization && Object.entries(item.customization).map(([key, value]) => (
                <div key={key} className="flex items-center gap-1">
                  <span className="font-medium capitalize">{key}:</span>
                  <span>{String(value)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-x-2">
              {item.size && (
                <span className="inline-flex items-center">
                  <span className="font-medium">Size:</span>
                  <span className="ml-1">{item.size}</span>
                </span>
              )}
              {item.color && (
                <span className="inline-flex items-center">
                  <span className="font-medium">{item.category?.toLowerCase() === 'full set' ? 'Design:' : 'Color:'}</span>
                  <span className="ml-1">{item.color}</span>
                </span>
              )}
            </div>
          )}
          <div className="mt-1 text-xs font-semibold">
            {formatCurrency(item.adjustedPrice || item.price || 0)} each
          </div>
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
        onClick={() => removeItem(item.id)}
        aria-label={`Remove ${item.name} from cart`}
      >
        <Trash2 className="h-4 w-4" />
        <span className="sr-only">Remove item</span>
      </Button>
    </div>
  </div>
));

// Extracted to a separate component for better performance
const QuantityControls = React.memo(({ 
  item, 
  onQuantityChange 
}: { 
  item: CartItem, 
  onQuantityChange: (itemId: string, newQuantity: number) => Promise<void> 
}) => {
  // Use direct DOM manipulation to prevent event bubbling
  const handleDecrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuantityChange(
      item.id,
      Math.max(1, item.quantity - 1)
    );
  };

  const handleIncrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuantityChange(
      item.id,
      item.quantity + 1
    );
  };

  return (
    <div className="flex items-center gap-1 border rounded-md overflow-hidden quantity-controls" onClick={(e) => e.stopPropagation()}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-none border-r"
        onClick={handleDecrease}
        disabled={item.quantity <= 1}
        aria-label="Decrease quantity"
      >
        <Minus className="h-3 w-3" />
        <span className="sr-only">Decrease quantity</span>
      </Button>
      <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-none border-l"
        onClick={handleIncrease}
        aria-label="Increase quantity"
      >
        <Plus className="h-3 w-3" />
        <span className="sr-only">Increase quantity</span>
      </Button>
    </div>
  );
});

// Extracted to a separate component for better performance
const CheckoutFooter = React.memo(({ total, onCheckout, isUpdatingQuantity }: { 
  total: number, 
  onCheckout: () => void,
  isUpdatingQuantity: boolean
}) => (
  <div className="bg-background/95 backdrop-blur-md sticky bottom-0 pt-2 pb-4 px-4 border-t">
    <div className="mb-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Total</span>
        <span className="text-sm font-bold">{formatCurrency(total)}</span>
      </div>
    </div>
    <Button 
      className="w-full h-9 text-sm font-medium shadow-sm group" 
      onClick={onCheckout}
      disabled={total <= 0 || isUpdatingQuantity}
    >
      Proceed to Checkout
      <ArrowRight className="ml-2 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
    </Button>
  </div>
));

export const CartDrawer: React.FC<CartDrawerProps> = React.memo(({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { 
    cartItems, 
    removeItem, 
    updateItem,
    isLoading: isCartLoading,
    error: cartError,
    addItem,
    clearCart
  } = useCart();
  const sheetRef = useRef<HTMLDivElement>(null);
  const [preventAutoClose, setPreventAutoClose] = useState(false);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Use React Query with staleTime for better performance
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: getProducts,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // When cart items change, scroll to top if drawer is open
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isOpen, cartItems.length]);

  const handleQuantityChange = async (
    itemId: string,
    newQuantity: number
  ) => {
    setIsUpdatingQuantity(true);
    setPreventAutoClose(true);
    try {
      if (newQuantity <= 0) {
        await removeItem(itemId);
      } else {
        await updateItem(itemId, { quantity: newQuantity });
      }
      setLocalError(null);
    } catch (error) {
      if (error instanceof Error) {
        setLocalError(error.message);
      }
    } finally {
      setIsUpdatingQuantity(false);
      // Reset prevent auto close after a delay
      setTimeout(() => {
        setPreventAutoClose(false);
      }, 1000);
    }
  };

  const handleSheetChange = useCallback((open: boolean) => {
    // Only close if not prevented
    if (!open && !preventAutoClose) {
      onClose();
    }
  }, [preventAutoClose, onClose]);

  // Add a click event listener to the document to prevent closing
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (preventAutoClose && sheetRef.current && !sheetRef.current.contains(e.target as Node)) {
        e.stopPropagation();
      }
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [preventAutoClose]);

  // Memoize total to prevent unnecessary recalculations
  const total = useMemo(() => 
    cartItems.reduce((sum, item) => sum + (item.adjustedPrice || item.price) * item.quantity, 0),
  [cartItems]);

  return (
    <Sheet 
      open={isOpen} 
      onOpenChange={handleSheetChange}
    >
      <SheetContent 
        ref={sheetRef}
        side="right" 
        className="w-full sm:max-w-lg md:max-w-xl flex flex-col p-0 border-l shadow-xl bg-background/95 backdrop-blur-md"
        onPointerDownOutside={(e) => {
          if (preventAutoClose || 
              (e.target instanceof Element && (
                e.target.closest('button') || 
                e.target.closest('.quantity-controls')
              ))
          ) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (preventAutoClose) {
            e.preventDefault();
          }
        }}
        style={{
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* Header */}
        <motion.div 
          className="flex-shrink-0 border-b bg-background/95 backdrop-blur-md sticky top-0 z-20"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
        >
          <div className="p-4 sm:p-5">
            <SheetHeader className="mb-1">
              <div className="flex items-center justify-between">
                <motion.div 
                  className="flex items-center gap-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
                >
                  <ShoppingBag className="h-5 w-5 text-primary" />
                  <SheetTitle className="text-lg font-semibold">Your Cart</SheetTitle>
                  <Badge variant="outline" className="ml-1 px-2 py-0 h-5">
                    {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                  </Badge>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
                >
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
                    onClick={onClose}
                    aria-label="Close cart"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </Button>
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2, ease: [0.23, 1, 0.32, 1] }}
              >
                <SheetDescription className="mt-1">
                  Manage your shopping cart items
                </SheetDescription>
              </motion.div>
            </SheetHeader>
          </div>
        </motion.div>

        {/* Cart Items */}
        <ScrollArea 
          className="flex-1" 
          ref={scrollRef}
          style={{
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <motion.div 
            className="p-4 sm:p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.2, ease: [0.23, 1, 0.32, 1] }}
          >
            {localError && (
              <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <p>{localError}</p>
              </div>
            )}
            
            {isCartLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <p className="text-sm text-muted-foreground">Loading your cart...</p>
              </div>
            ) : cartItems.length === 0 ? (
              <EmptyCartMessage onClose={onClose} navigate={navigate} />
            ) : (
              <AnimatePresence initial={false}>
                <div className="space-y-4">
                  {cartItems.map((item, index) => (
                    <CartItemComponent
                      key={item.id}
                      variants={cartItemVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      layout
                      className="rounded-lg hover:bg-muted/30 transition-colors p-2"
                    >
                      {index > 0 && <Separator className="mb-4" />}
                      <div className="flex gap-4 py-2">
                        <CartItemImage item={item} />
                        <div className="flex flex-1 flex-col">
                          <CartItemDetails item={item} removeItem={removeItem} />
                          <div className="mt-auto pt-2 flex items-center justify-between">
                            {item.type !== 'tale' ? (
                              <QuantityControls item={item} onQuantityChange={handleQuantityChange} />
                            ) : (
                              <div className="text-sm text-muted-foreground">
                                Qty: {item.quantity}
                              </div>
                            )}
                            <p className="text-sm font-medium">
                              {formatCurrency((item.adjustedPrice || item.price || 0) * item.quantity)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CartItemComponent>
                  ))}
                </div>
              </AnimatePresence>
            )}
          </motion.div>
        </ScrollArea>

        {/* Footer with checkout */}
        {cartItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3, ease: [0.23, 1, 0.32, 1] }}
          >
            <CheckoutFooter 
              total={total} 
              onCheckout={() => {
                onClose();
                navigate("/checkout");
              }}
              isUpdatingQuantity={isUpdatingQuantity}
            />
          </motion.div>
        )}
      </SheetContent>
    </Sheet>
  );
});